import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { compareCompetitions } from '../../utils/competitionComparer';
import FileUpload from '../FileUpload';

const ImportModal = ({ open, onClose, onImport, onFileSelect, existingCompetitions = [], currentCompetition }) => {
  const fileUploadRef = React.useRef();
  
  const handleClose = () => {
    setError(null);
    setShowMergePrompt(false);
    setDuplicateData(null);
    if (fileUploadRef.current) {
      fileUploadRef.current.reset();
    }
    onClose();
  };
  const [isProcessing, setIsProcessing] = useState(false);
  const [showMergePrompt, setShowMergePrompt] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);
  const [differences, setDifferences] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (duplicateData) {
      const existingCompetition = existingCompetitions?.find(
        comp => comp?.competition?.name === duplicateData?.parsedData?.competition?.name
      );
      if (existingCompetition) {
        const diffs = compareCompetitions(existingCompetition, duplicateData.parsedData);
        setDifferences(diffs);
      }
    } else {
      setDifferences(null);
    }
  }, [duplicateData, existingCompetitions]);

  const handleMerge = () => {
    // console.log("Merge triggered with data:", duplicateData?.parsedData);
    if (duplicateData?.parsedData?.competition?.name) {
      const existingCompetition = existingCompetitions.find(
        comp => comp?.competition?.name === duplicateData.parsedData.competition.name
      );
      if (existingCompetition) {
        const diffs = compareCompetitions(existingCompetition, duplicateData.parsedData);
        setDifferences(diffs);
        if (Object.keys(diffs).length > 0) {
          // Only proceed with merge if there are actual differences
          // console.log("Calling onImport with merged data:", duplicateData.parsedData);
          onImport(duplicateData.parsedData);
          setShowMergePrompt(false);
          setDuplicateData(null);
          onClose();
        } else {
          // No differences found
          setError("No differences found between existing and imported competition");
        }
      }
    }
  };

  const handleOverwrite = () => {
    // console.log("handleOverwrite called");
    // console.log("duplicateData:", duplicateData);
    // console.log("existingCompetitions:", existingCompetitions);
    
    if (duplicateData?.parsedData) {
      // console.log("parsedData:", duplicateData.parsedData);
      // Find and replace the existing competition
      const existingCompetition = existingCompetitions?.find(comp => {
        // console.log("checking competition:", comp);
        return comp?.competition?.name === duplicateData?.parsedData?.competition?.name;
      });

      // console.log("found existingCompetition:", existingCompetition);
      if (existingCompetition) {
        onImport({
          ...duplicateData.parsedData,
          competition: {
            ...duplicateData.parsedData.competition,
            id: existingCompetition.competition.id // Preserve the original ID
          }
        }, true); // true indicates overwrite
      }
      setShowMergePrompt(false);
      setDuplicateData(null);
      onClose();
    }
  };
  return (
    <Dialog
      data-ui="modal-import"
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          transition: 'border 0.2s ease',
        }
      }}
    >
      <DialogTitle>
        {showMergePrompt ? 'Duplicate Competition Detected' : 'Import Competition'}
      </DialogTitle>
      <DialogContent data-ui="modal-import-content">
        {error && !showMergePrompt && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {showMergePrompt ? (
          <DialogContentText>
            A competition with the name "{duplicateData?.parsedData?.competition?.name}" already exists.&nbsp;
            {Object.keys(differences || {}).length > 0 ? (
              <>
                Below are the differences between the existing and new competition data.
                Would you like to merge these changes with the existing competition or overwrite it completely?
              </>
            ) : (
              "No differences were found between the existing and imported competition data."
            )}
          </DialogContentText>
        ) : (
          <DialogContentText sx={{ mb: 3 }}>
            Import a competition file in JSON, CSV, or PTF format.
            Click the button below or drag and drop your file to begin.
          </DialogContentText>
        )}
        {differences && Object.entries(differences).map(([section, diffs]) => (
          <Accordion key={section} sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ textTransform: 'capitalize' }}>
                {section} ({diffs.length} changes)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Current Value</TableCell>
                      <TableCell>New Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {diffs.map((diff, index) => (
                      <TableRow key={index}>
                        <TableCell>{diff.path}</TableCell>
                        <TableCell>{JSON.stringify(diff.oldValue)}</TableCell>
                        <TableCell>{JSON.stringify(diff.newValue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, px: 3 }}>
          {isProcessing ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              py: 4
            }}>
              <CircularProgress />
              <Typography>
                Processing file...
              </Typography>
            </Box>
          ) : (
            <FileUpload
              ref={fileUploadRef}
              onImport={onImport}
              existingCompetitions={existingCompetitions}
              showMergePrompt={showMergePrompt}
              currentCompetition={currentCompetition}
              onFileSelect={async (file) => {
                setIsProcessing(true);
                try {
                  await onFileSelect(file);
                } finally {
                  setIsProcessing(false);
                }
              }}
              onDuplicateFound={(data) => {
                setDuplicateData(data);
                setShowMergePrompt(true);
              }}
              acceptedFormats=".json,.csv,.ptf"
            />
          )}
        </Box>
      </DialogContent>
      {showMergePrompt && (
        <DialogActions>
          {Object.keys(differences || {}).length > 0 ? (
            <>
              <Button data-ui="btn-import-overwrite" onClick={handleOverwrite} color="warning">
                Overwrite
              </Button>
              <Button data-ui="btn-import-merge" onClick={handleMerge} color="primary">
                Merge
              </Button>
            </>
          ) : null}
          <Button data-ui="btn-import-cancel" onClick={() => {
            setShowMergePrompt(false);
            setDuplicateData(null);
            setError(null);
            if (fileUploadRef.current) {
              fileUploadRef.current.reset();
            }
          }} color="primary">
            Cancel
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ImportModal;
